import {
  ActionIcon,
  Button,
  Chip,
  Loader,
  Modal,
  Table,
  Tabs,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core"
import {
  IconBellRinging,
  IconDownload,
  IconLink,
  IconTrash,
  IconUpload,
} from "@tabler/icons"
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { appFlavors, BOTTOM_BAR_HEIGHT } from "../../miscellaneous/constants"
import AuthenticationContext from "../AuthenticationContext"
import { useFileQuery } from "./FileList.hooks"
import Props, { FileSection, LocalizedNotification } from "./FileList.types"
import { AppFlavor } from "../../miscellaneous/types"

const TABLE_MARGIN = 16

const supportedLanguages = [
  {
    value: "it",
    label: "Italiano",
  },
  {
    value: "es",
    label: "Español",
  },
] as const

const deleteResource = (
  fileId: string,
  token: string,
  setLoading: Dispatch<SetStateAction<boolean>>,
  refetchFiles: () => void,
  route: "files" | "links",
) => {
  setLoading(true)

  fetch(`https://resinglass-api.igloo.ooo/${route}/${fileId}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
    },
  })
    .then(refetchFiles)
    .finally(() => setLoading(false))
}

const sendFileNotification = (
  id: string,
  token: string,
  title: LocalizedNotification,
  content: LocalizedNotification,
  setError: Dispatch<SetStateAction<boolean>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
) => {
  setLoading(true)

  fetch(`https://resinglass-api.igloo.ooo/links/${id}/publishNotification`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: `{"title":"${title}","content":"${content}"}`,
  })
    .then(({ ok }) => {
      if (!ok) setError(true)
    })
    .catch(() => setError(true))
    .finally(() => setLoading(false))
}

const uploadFile = (
  token: string,
  file: File,
  section: FileSection,
  refetchFiles: () => void,
  masterOnly: boolean,
  app: AppFlavor[],
) => {
  const form = new FormData()
  form.append("file", file)

  return fetch(
    `https://resinglass-api.igloo.ooo/files?section=${section}&visibleTo=${
      masterOnly ? "master" : "guest"
    }&apps=${app.join(",")}`,
    {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
      },
      body: form,
    },
  ).then(() => refetchFiles())
}

const uploadLink = (
  token: string,
  section: FileSection,
  refetchFiles: () => void,
  masterOnly: boolean,
  apps: AppFlavor[],
  description: string,
  url: string,
) =>
  fetch(`https://resinglass-api.igloo.ooo/links`, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      section,
      visibleTo: masterOnly ? "master" : "guest",
      apps: apps.join(","),
      description,
      url,
    }),
  }).then(() => refetchFiles())

const FileList = ({ mobile, section }: Props) => {
  const { t } = useTranslation()
  const [token] = useContext(AuthenticationContext)
  const uploadRef = useRef<HTMLInputElement>(null)

  const [fileNotificationDialogOpen, setFileNotificationDialogOpen] =
    useState<boolean>(false)
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [masterOnly, setMasterOnly] = useState<boolean>(true)
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false)
  const [selectedApps, setSelectedApps] = useState<AppFlavor[]>([
    appFlavors[0].value,
  ])
  const [selectedResource, setSelectedResource] = useState<string>("")
  const [title, setTitle] = useState<LocalizedNotification>({
    es: "",
    it: "",
  })
  const [content, setContent] = useState<LocalizedNotification>({
    es: "",
    it: "",
  })
  const [fileNotificationError, setFileNotificationError] =
    useState<boolean>(false)
  const [fileNotificationLoading, setFileNotificationLoading] =
    useState<boolean>(false)
  const [newLinkDialogOpen, setNewLinkDialogOpen] = useState<boolean>(false)
  const [newLinkDescription, setNewLinkDescription] = useState<string>("")
  const [newLinkURL, setNewLinkURL] = useState<string>("")

  const {
    loading: fileLoading,
    error,
    files,
    refetch: refetchFiles,
  } = useFileQuery(token, section)

  useEffect(() => {
    if (uploadModalOpen) uploadRef.current!.files = null
  }, [uploadModalOpen])

  return (
    <div
      style={{
        width: `calc(100% - ${TABLE_MARGIN * 2}px)`,
        height: `calc(100% - ${TABLE_MARGIN * 2}px)`,
        justifyContent: "center",
        margin: TABLE_MARGIN + "px",
      }}
    >
      {fileLoading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Loader />
        </div>
      ) : error ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Title order={1} style={{ marginBottom: "24px" }}>
            {t`Errore`}
          </Title>
        </div>
      ) : files.length ? (
        <Table
          style={{
            margin: "auto",
            maxWidth: "800px",
          }}
        >
          <thead>
            <tr>
              <th>{t`Nome` as string}</th>
              <th>{t`Visibile a` as string}</th>
              <th>{t`App` as string}</th>
              {section === "video" && <th>{t`Tipo` as string}</th>}
              <th style={{ textAlign: "right" }}>{t`Azioni` as string}</th>
            </tr>
          </thead>
          <tbody>
            {files.map(({ id, description, link, apps, visibleTo, kind }) => (
              <tr key={"video-list-item-" + id}>
                <td style={{ width: "calc(100% / 3)" }}>{description}</td>
                <td>
                  {t(visibleTo === "master" ? "Proprietario" : "Famigliari")}
                </td>
                <td>
                  {apps
                    .map(
                      (app) =>
                        appFlavors.find(({ value }) => value === app)?.label,
                    )
                    .join(", ")}
                </td>
                {section === "video" && (
                  <td>{t(kind === "link" ? "Link" : "File")}</td>
                )}
                <td style={{ display: "flex", justifyContent: "right" }}>
                  {kind === "file" ? (
                    <ActionIcon
                      style={{ color: "black" }}
                      component="a"
                      href={`https://resinglassstorage.blob.core.windows.net/appfiles/${id}.${description}`}
                      target="_blank"
                    >
                      <IconDownload />
                    </ActionIcon>
                  ) : (
                    <>
                      <ActionIcon
                        style={{ color: "black" }}
                        onClick={() => {
                          setFileNotificationDialogOpen(true)
                          setSelectedResource(id)
                        }}
                      >
                        <IconBellRinging />
                      </ActionIcon>
                      <ActionIcon
                        style={{ marginLeft: "8px", color: "black" }}
                        onClick={() => window.open(link, "_blank")?.focus()}
                      >
                        <IconLink />
                      </ActionIcon>
                    </>
                  )}
                  <ActionIcon
                    style={{ marginLeft: "8px", color: "black" }}
                    onClick={() =>
                      deleteResource(
                        id,
                        token,
                        setDeleteLoading,
                        refetchFiles,
                        kind === "file" ? "files" : "links",
                      )
                    }
                    loading={deleteLoading}
                  >
                    <IconTrash />
                  </ActionIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Title order={1} style={{ marginBottom: "24px" }}>
            {t`Nessun file`}
          </Title>
        </div>
      )}
      {section === "video" && (
        <Button
          leftIcon={<IconLink />}
          variant="filled"
          style={{
            width: "min-content",
            backgroundColor: "var(--main-color)",
            color: "var(--contrasting-color)",
            borderRadius: "16px",
            position: "absolute",
            bottom: (mobile ? BOTTOM_BAR_HEIGHT : 0) + 88,
            ...(mobile
              ? {
                  left: 0,
                  right: 0,
                  marginLeft: "auto",
                  marginRight: "auto",
                }
              : { right: 24 }),
          }}
          sx={(theme) => ({ boxShadow: theme.shadows.md })}
          size="lg"
          onClick={() => setNewLinkDialogOpen(true)}
        >
          {t`Carica link`}
        </Button>
      )}
      <Button
        leftIcon={<IconUpload />}
        variant="filled"
        style={{
          width: "min-content",
          backgroundColor: "var(--main-color)",
          color: "var(--contrasting-color)",
          borderRadius: "16px",
          position: "absolute",
          bottom: (mobile ? BOTTOM_BAR_HEIGHT : 0) + 24,
          ...(mobile
            ? {
                left: 0,
                right: 0,
                marginLeft: "auto",
                marginRight: "auto",
              }
            : { right: 24 }),
        }}
        sx={(theme) => ({ boxShadow: theme.shadows.md })}
        size="lg"
        onClick={() => setUploadModalOpen(true)}
      >
        {t`Carica` +
          " " +
          (section === "video"
            ? "(*.mp4)"
            : section === "faq"
            ? "(*.pdf)"
            : "(*.pdf)")}
      </Button>
      <Modal
        title={t`Invia notifica caricamento file`}
        opened={fileNotificationDialogOpen}
        onClose={() => setFileNotificationDialogOpen(false)}
        centered
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Tabs defaultValue={supportedLanguages[0].value}>
            <Tabs.List style={{ marginBottom: 8 }}>
              {supportedLanguages.map(({ label, value }) => (
                <Tabs.Tab {...{ value }}>{label}</Tabs.Tab>
              ))}
            </Tabs.List>
            {supportedLanguages.map(({ value }) => (
              <Tabs.Panel {...{ value }}>
                <TextInput
                  value={title[value]}
                  onChange={(event) => {
                    setTitle((title) => ({
                      ...title,
                      [value]: event.target.value,
                    }))
                    setFileNotificationError(false)
                  }}
                  onKeyDown={({ code }) => {
                    if (code === "Enter") {
                      sendFileNotification(
                        selectedResource,
                        token,
                        title,
                        content,
                        setFileNotificationError,
                        setFileNotificationLoading,
                      )
                    }
                  }}
                  label={t`Titolo` as String}
                  size="md"
                  error={fileNotificationError}
                />
                <Textarea
                  value={content[value]}
                  onChange={(event) => {
                    setContent((content) => ({
                      ...content,
                      [value]: event.target.value,
                    }))
                    setFileNotificationError(false)
                  }}
                  onKeyDown={({ code }) => {
                    if (code === "Enter") {
                      sendFileNotification(
                        selectedResource,
                        token,
                        title,
                        content,
                        setFileNotificationError,
                        setFileNotificationLoading,
                      )
                    }
                  }}
                  label={t`Contenuto` as String}
                  error={fileNotificationError}
                  size="md"
                  style={{
                    margin: "24px 0 12px",
                    paddingBottom: error ? 0 : "21px",
                  }}
                  autosize
                  minRows={4}
                />
              </Tabs.Panel>
            ))}
          </Tabs>
          <Button
            onClick={() =>
              sendFileNotification(
                selectedResource,
                token,
                title,
                content,
                setFileNotificationError,
                setFileNotificationLoading,
              )
            }
            loading={fileNotificationLoading}
            disabled={Object.values({ ...title, ...content }).some(
              (title) => !title,
            )}
          >{t`Invia notifica`}</Button>
        </div>
      </Modal>
      <Modal
        title={t`Carica link`}
        opened={newLinkDialogOpen}
        onClose={() => setNewLinkDialogOpen(false)}
        centered
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <TextInput
            value={newLinkDescription}
            onChange={(event) => setNewLinkDescription(event.target.value)}
            onKeyDown={({ code }) => {
              if (code === "Enter") {
                sendFileNotification(
                  selectedResource,
                  token,
                  title,
                  content,
                  setFileNotificationError,
                  setFileNotificationLoading,
                )
              }
            }}
            label={t`Descrizione` as String}
            size="md"
            error={error}
            style={{}}
          />
          <TextInput
            value={newLinkURL}
            onChange={(event) => setNewLinkURL(event.target.value)}
            onKeyDown={({ code }) => {
              if (code === "Enter") {
                sendFileNotification(
                  selectedResource,
                  token,
                  title,
                  content,
                  setFileNotificationError,
                  setFileNotificationLoading,
                )
              }
            }}
            label={t`Indirizzo` as String}
            size="md"
            error={error}
            style={{}}
          />
          {t`Visibile a:`}
          <Chip.Group>
            <Chip checked={masterOnly} onClick={() => setMasterOnly(true)}>
              {t`Proprietario`}
            </Chip>
            <Chip checked={!masterOnly} onClick={() => setMasterOnly(false)}>
              {t`Famigliari`}
            </Chip>
          </Chip.Group>
          {t`Disponibile per:`}
          <Chip.Group>
            {appFlavors.map(({ value, label }) => (
              <Chip
                checked={selectedApps.includes(value)}
                onClick={() =>
                  setSelectedApps((selectedApps) =>
                    selectedApps.includes(value)
                      ? selectedApps.length > 1
                        ? selectedApps.filter((app) => app !== value)
                        : selectedApps
                      : [...selectedApps, value],
                  )
                }
              >
                {label}
              </Chip>
            ))}
          </Chip.Group>
          <Button
            onClick={() =>
              uploadLink(
                token,
                section,
                refetchFiles,
                masterOnly,
                selectedApps,
                newLinkDescription,
                newLinkURL,
              ).then(() => {
                setNewLinkDialogOpen(false)
                setNewLinkDescription("")
                setNewLinkURL("")
              })
            }
            loading={fileNotificationLoading}
            disabled={!newLinkDescription || !newLinkURL}
          >{t`Carica link`}</Button>
        </div>
      </Modal>
      <Modal
        title={t`Carica file`}
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        centered
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {t`Visibile a:`}
          <Chip.Group>
            <Chip checked={masterOnly} onClick={() => setMasterOnly(true)}>
              {t`Proprietario`}
            </Chip>
            <Chip checked={!masterOnly} onClick={() => setMasterOnly(false)}>
              {t`Famigliari`}
            </Chip>
          </Chip.Group>
          {t`Disponibile per:`}
          <Chip.Group>
            {appFlavors.map(({ value, label }) => (
              <Chip
                checked={selectedApps.includes(value)}
                onClick={() =>
                  setSelectedApps((selectedApps) =>
                    selectedApps.includes(value)
                      ? selectedApps.length > 1
                        ? selectedApps.filter((app) => app !== value)
                        : selectedApps
                      : [...selectedApps, value],
                  )
                }
              >
                {label}
              </Chip>
            ))}
          </Chip.Group>
          <Button
            onClick={() => uploadRef.current?.click()}
          >{t`Carica`}</Button>
        </div>
      </Modal>
      <input
        type="file"
        accept={
          section === "video"
            ? "video/mp4,video/x-m4v,video/*"
            : "application/pdf"
        }
        ref={uploadRef}
        style={{ display: "none" }}
        onChange={(event) => {
          if (event.target.files?.length) {
            uploadFile(
              token,
              event.target.files[0],
              section,
              refetchFiles,
              masterOnly,
              selectedApps,
            ).then(() => setUploadModalOpen(false))
          }
        }}
      />
    </div>
  )
}

export default FileList
